import { call, put, takeEvery, all } from 'redux-saga/effects';
import urls from 'config/urls';
import {
  awsLoginRequest,
  signOut as signOutRequest,
  submitSignInChallenge,
  confirmTotpSetupViaSignIn,
  selectMfaMethod as selectMfaMethodRequest,
  restartSignInWithMethod,
  getMfaStatus,
  isMfaRequired,
  rememberThisDevice,
  mapContract,
  evaluateBlocks,
  resolveMfaRoute,
  resolveErrorMessage,
  startAuthFlow,
  clearAuthFlow,
  AUTH_STEP_CHALLENGE,
  AUTH_STEP_SETUP,
} from 'utils/aws';
import { DashboardConstants } from 'constants/dashboard';
import { AuthConstants } from 'constants/auth';
import { request, requestPending, requestSuccess, requestFail } from 'components/Fetch';
import { getConfig } from 'config/configService';
import { formatPhone } from 'utils/masks';

function persistSessionContract(tokens) {
  const contract = mapContract(tokens);
  Object.keys(contract).forEach((key) => {
    sessionStorage.setItem(key, contract[key]);
  });
}

function* postAuthentication(tokens, { success, fail, navigate, mfaJustEnrolled = false }) {
  persistSessionContract(tokens);
  clearAuthFlow();
  let route = '/consumer/dashboard';
  if (mfaJustEnrolled) {
    sessionStorage.setItem('mfaSatisfied', 'true');
  } else {
    let status;
    let mfaRequired;
    try {
      [status, mfaRequired] = yield all([
        call(getMfaStatus),
        call(isMfaRequired),
      ]);
    } catch (err) {
      yield put({ type: AuthConstants.AUTH_SIGN_OUT });
      fail({ message: resolveErrorMessage(err) });
      return;
    }

    const resolved = resolveMfaRoute(status, mfaRequired);
    route = resolved.route;
    sessionStorage.setItem('mfaSatisfied', resolved.mfaSatisfied);
    if (resolved.mfaSatisfied === 'false') {
      navigate(route);
      return;
    }
  }

  const [customerDetail, loansList] = yield all([
    call(request({
      type: DashboardConstants.FETCH_CUSTOMER_DETAIL,
      method: 'GET',
      url: urls.fetchCustomerDetail(),
    })),
    call(request({
      type: DashboardConstants.FETCH_LOANS_LIST,
      method: 'GET',
      url: urls.fetchLoansListUrl(),
    })),
  ]);

  const blocks = evaluateBlocks(customerDetail, loansList);
  if (blocks.blocked) {
    yield put({ type: AuthConstants.AUTH_SIGN_OUT });
    const { massachusettsErrorPhone, servicingPhone } = getConfig();
    if (blocks.reason === 'MA') {
      fail({ message: `Massachusetts residents, we are unable to receive payments through this site. Your payment should be made as provided in your monthly statement. If you have questions regarding your loan or want to make payment, please call ${massachusettsErrorPhone}` });
    } else {
      fail({ message: `You currently do not have any active loans. If you have any questions, please call us at ${formatPhone(servicingPhone)}` });
    }
    return;
  }

  sessionStorage.setItem('customerDetail', JSON.stringify(customerDetail));
  sessionStorage.setItem('loansList', JSON.stringify(loansList));

  if (route !== '/consumer/dashboard') {
    navigate(route);
    return;
  }

  success('success');
}

function routeChallenge(navigate, challenge, ctx) {
  const { type, destination } = challenge;
  const { username, password, allowedMFATypes = [] } = ctx || {};

  startAuthFlow(AUTH_STEP_CHALLENGE);

  if (type === 'EMAIL') {
    navigate('/two-factor/verify-by-email', {
      state: {
        challengeType: 'EMAIL',
        username,
        password,
        mode: 'challenge',
        email: destination,
        allowedMFATypes,
      },
    });
    return;
  }

  navigate('/two-factor/verify', {
    state: {
      challengeType: 'TOTP',
      username,
      password,
      allowedMFATypes,
    },
  });
}

function navigateTotpSetup(navigate, totpSetup) {
  startAuthFlow(AUTH_STEP_SETUP);
  navigate('/two-factor/setup-authenticator', {
    state: {
      viaSignIn: true,
      setupUri: totpSetup.setupUri,
      sharedSecret: totpSetup.sharedSecret,
    },
  });
}

function* signIn({ payload }) {
  const username = payload && payload.data && payload.data.username;
  const password = payload && payload.data && payload.data.password;
  try {
    yield put({
      type: requestPending(AuthConstants.AUTH_SIGN_IN),
    });

    const outcome = yield call(awsLoginRequest, { payload: payload.data });

    if (outcome && outcome.error === 'New password required') {
      payload.navigate('/set-new-password');
      yield put({
        type: requestFail(AuthConstants.AUTH_SIGN_IN),
        payload: new Error('New password required'),
      });
      sessionStorage.setItem('user.username', username);
      return;
    }

    if (outcome && outcome.totpSetup) {
      navigateTotpSetup(payload.navigate, outcome.totpSetup);
      yield put({
        type: requestFail(AuthConstants.AUTH_SIGN_IN),
        payload: new Error('MFA setup required'),
      });
      return;
    }

    if (outcome && outcome.mfaSelection) {
      const { allowedMFATypes } = outcome.mfaSelection;
      const prefersTotp = allowedMFATypes.includes('TOTP');
      const chosen = prefersTotp ? 'TOTP' : allowedMFATypes[0];

      let selected;
      try {
        selected = yield call(selectMfaMethodRequest, chosen);
      } catch (err) {
        yield put({
          type: requestFail(AuthConstants.AUTH_SIGN_IN),
          payload: err,
        });
        payload.fail({ message: resolveErrorMessage(err, 'Unable to start verification. Please sign in again.') });
        return;
      }

      if (selected && selected.challenge) {
        routeChallenge(payload.navigate, selected.challenge, {
          username,
          password,
          allowedMFATypes,
        });
        yield put({
          type: requestFail(AuthConstants.AUTH_SIGN_IN),
          payload: new Error('MFA challenge required'),
        });
        return;
      }
      yield put({
        type: requestFail(AuthConstants.AUTH_SIGN_IN),
        payload: new Error('Unable to start verification'),
      });
      payload.fail({ message: 'Unable to start verification. Please sign in again.' });
      return;
    }

    if (outcome && outcome.challenge) {
      routeChallenge(payload.navigate, outcome.challenge, { username, password });
      yield put({
        type: requestFail(AuthConstants.AUTH_SIGN_IN),
        payload: new Error('MFA challenge required'),
      });
      return;
    }

    if (outcome && outcome.error === 'Unrecognized sign-in step') {
      yield put({
        type: requestFail(AuthConstants.AUTH_SIGN_IN),
        payload: new Error(outcome.error),
      });
      payload.fail({ message: resolveErrorMessage({ message: 'Unable to sign in. Please try again.' }) });
      return;
    }

    yield put({
      type: requestSuccess(AuthConstants.AUTH_SIGN_IN),
      payload: outcome,
    });
    yield* postAuthentication(outcome, {
      success: payload.success,
      fail: payload.fail,
      navigate: payload.navigate,
    });
  } catch (err) {
    yield put({
      type: requestFail(AuthConstants.AUTH_SIGN_IN),
      payload: err,
    });
    if (username) {
      sessionStorage.setItem('user.username', username);
    }
    payload.fail(err);
  }
}

function* completeMfaChallenge({ payload }) {
  const {
    code, trustDevice, success, fail, navigate, viaTotpSetup,
  } = payload;
  try {
    yield put({
      type: requestPending(AuthConstants.AUTH_SIGN_IN),
    });

    const result = yield call(
      viaTotpSetup ? confirmTotpSetupViaSignIn : submitSignInChallenge,
      code
    );

    if (!result || !result.idToken) {
      yield put({
        type: requestFail(AuthConstants.AUTH_SIGN_IN),
        payload: new Error('Please sign in again.'),
      });
      fail({ message: 'Your session could not be completed. Please sign in again.' });
      return;
    }

    if (trustDevice) {
      try {
        yield call(rememberThisDevice);
      } catch (err) {
        // Non-fatal: remembering the device is best-effort and must not block sign-in.
      }
    }

    yield put({
      type: requestSuccess(AuthConstants.AUTH_SIGN_IN),
      payload: result,
    });

    yield* postAuthentication(result, {
      success, fail, navigate, mfaJustEnrolled: viaTotpSetup,
    });
  } catch (err) {
    yield put({
      type: requestFail(AuthConstants.AUTH_SIGN_IN),
      payload: err,
    });
    fail(err);
  }
}

function* selectMfaMethod({ payload }) {
  const { mfaType, username, password, allowedMFATypes, navigate, fail } = payload;
  try {
    yield put({
      type: requestPending(AuthConstants.AUTH_SIGN_IN),
    });

    const selected = yield call(selectMfaMethodRequest, mfaType);

    if (selected && selected.challenge) {
      routeChallenge(navigate, selected.challenge, {
        username,
        password,
        allowedMFATypes,
      });
      yield put({
        type: requestFail(AuthConstants.AUTH_SIGN_IN),
        payload: new Error('MFA challenge required'),
      });
      return;
    }

    yield put({
      type: requestFail(AuthConstants.AUTH_SIGN_IN),
      payload: new Error('Unable to switch verification method'),
    });
    fail({ message: 'Unable to switch verification method. Please try again.' });
  } catch (err) {
    yield put({
      type: requestFail(AuthConstants.AUTH_SIGN_IN),
      payload: err,
    });
    fail(err);
  }
}

function* switchMfaMethod({ payload }) {
  const { mfaType, username, password, navigate, fail } = payload;
  try {
    yield put({
      type: requestPending(AuthConstants.AUTH_SIGN_IN),
    });

    const outcome = yield call(restartSignInWithMethod, username, password, mfaType);

    if (outcome && outcome.challenge) {
      routeChallenge(navigate, outcome.challenge, {
        username,
        password,
        allowedMFATypes: [],
      });
      yield put({
        type: requestFail(AuthConstants.AUTH_SIGN_IN),
        payload: new Error('MFA challenge required'),
      });
      return;
    }

    yield put({
      type: requestFail(AuthConstants.AUTH_SIGN_IN),
      payload: new Error('Unable to switch verification method'),
    });
    fail({ message: 'Switching verification methods is not available for your account. Please use the current method or contact support.' });
  } catch (err) {
    yield put({
      type: requestFail(AuthConstants.AUTH_SIGN_IN),
      payload: err,
    });
    fail(err);
  }
}

function* signOut({ payload }) {
  const { navigate } = payload || {};

  yield call(signOutRequest);

  sessionStorage.clear();

  yield put({ type: requestSuccess(AuthConstants.AUTH_SIGN_OUT) });

  if (navigate) navigate('/');
}

const authSaga = function* () {
  yield takeEvery(AuthConstants.AUTH_SIGN_IN, signIn);
  yield takeEvery(AuthConstants.AUTH_COMPLETE_MFA_CHALLENGE, completeMfaChallenge);
  yield takeEvery(AuthConstants.AUTH_SELECT_MFA_METHOD, selectMfaMethod);
  yield takeEvery(AuthConstants.AUTH_SWITCH_MFA_METHOD, switchMfaMethod);
  yield takeEvery(AuthConstants.AUTH_SIGN_OUT, signOut);
};

export default authSaga;
